import {
  type AdminApiMinimalPlaybook,
  type AdminApiPlaybook,
  type AdminApiPlaybookPublishedVersion,
  PlaybookBranch,
  type PlaybookCreatePayload,
  type PlaybookId,
  type PlaybookNode,
  type PlaybookNodeId,
  type PlaybookUpdatePayload,
  type PlaybookView,
} from "@repo/app-types/entities/playbook";
import type {
  PlaybookNodeSampleToolResultId,
  PlaybookNodeSampleToolResultPayload,
  SampleToolResultGradingsPayload,
} from "@repo/app-types/entities/playbookNodeSampleToolResult";
import type { PlaybookViewFilterDefaults } from "@repo/app-types/entities/playbookViewDefaults";
import type {
  FeedbackWithCurrentGrade,
  TestGradingResult,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import { fetchEventStream } from "@repo/ui/api/eventStream";
import {
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";
import { apiBaseUrl, getApiAuthorizationHeaderValue } from "../shared.js";

const playbookApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Playbook endpoints
    // TODO: Add optimistic updates
    allPlaybooks: builder.query<
      AdminApiPlaybook[],
      { exclude_teammate_agent_playbooks: string },
      { data: { recording_assessment_templates: AdminApiPlaybook[] } }
    >({
      query: ({ exclude_teammate_agent_playbooks }) => ({
        url: "/recording-assessment-templates",
        params: {
          exclude_teammate_agent_playbooks,
        },
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_templates,
      providesTags: (result) => getTagsProvidedByList(TagType.Playbook, result),
    }),

    allMinimalPlaybooks: builder.query<
      AdminApiMinimalPlaybook[],
      { shouldExcludeTeammateAgentPlaybooks?: boolean } | void,
      { data: { recording_assessment_templates: AdminApiMinimalPlaybook[] } }
    >({
      query: ({ shouldExcludeTeammateAgentPlaybooks } = {}) => ({
        method: "GET",
        url: "/recording-assessment-templates",
        params: {
          format: "minimal",
          exclude_teammate_agent_playbooks: shouldExcludeTeammateAgentPlaybooks
            ? "true"
            : undefined,
        },
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_templates,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.MinimalPlaybook, result),
    }),

    playbookById: builder.query<
      AdminApiPlaybook,
      | string
      | {
          /** ID of the playbook to fetch. */
          id: string;
          /** The branch to fetch the head revision from. Defaults to the live revision. */
          branch?: PlaybookBranch;
          /** ID of the revision to fetch. Takes precedence over branch. */
          revisionId?: string;
        },
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: (idOrArg) => {
        const { id, branch, revisionId } =
          typeof idOrArg === "string" ? { id: idOrArg } : idOrArg;

        return {
          url: `/recording-assessment-templates/${id}`,
          params: {
            branch,
            revision_id: revisionId,
          },
        };
      },
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      providesTags: (_result, _error, idOrArg) =>
        getTagsProvidedByEntity(
          TagType.Playbook,
          typeof idOrArg === "string" ? idOrArg : idOrArg.id,
        ),
    }),

    createPlaybook: builder.mutation<
      AdminApiPlaybook,
      PlaybookCreatePayload,
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: (body) => ({
        method: "POST",
        url: "/recording-assessment-templates",
        body,
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.Playbook),
    }),

    updatePlaybook: builder.mutation<
      AdminApiPlaybook,
      {
        /** ID of a playbook. */
        id: string;
        /** The branch to update with the new revision. Defaults to the live branch for backward compatibility. */
        branch?: PlaybookBranch;
      } & PlaybookUpdatePayload,
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: ({ id, branch, ...body }) => ({
        method: "PATCH",
        url: `/recording-assessment-templates/${encodeURIComponent(id)}`,
        params: {
          branch,
        },
        body,
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      onQueryStarted: async (
        { id, branch = PlaybookBranch.Live },
        { dispatch, queryFulfilled },
      ) => {
        const { data: updatedPlaybook } = await queryFulfilled;

        if (branch === PlaybookBranch.Live) {
          dispatch(
            playbookApi.util.invalidateTags(
              getTagsInvalidatedByUpdate(TagType.Playbook, id),
            ),
          );
        } else {
          dispatch(
            playbookApi.util.updateQueryData(
              "playbookById",
              { id, branch },
              () => updatedPlaybook,
            ),
          );
        }
      },
      invalidatesTags: () => [
        // Invalidate only the list, since we already update the entity in onQueryStarted (and invalidating the entity breaks that for some reason)
        // getTagsInvalidatedByUpdate(TagType.Playbook, id),
        buildListTag(TagType.Playbook, []),
      ],
    }),

    /**
     * Set the playbook's default date filter for one kind of page. Its own
     * endpoint because view defaults sit outside the revision lifecycle —
     * saving one must not cut a revision.
     */
    updatePlaybookViewDefaults: builder.mutation<
      AdminApiPlaybook,
      {
        id: PlaybookId;
        view: PlaybookView;
        defaults: PlaybookViewFilterDefaults;
      },
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: ({ id, view, defaults }) => ({
        method: "PATCH",
        url: `/recording-assessment-templates/${encodeURIComponent(id)}/view-defaults`,
        body: { view, defaults },
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      invalidatesTags: (_result, _error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.Playbook, id),
    }),

    deletePlaybook: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/recording-assessment-templates/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (result, error, id) =>
        getTagsInvalidatedByDelete(TagType.Playbook, id),
    }),

    revertPlaybookBranch: builder.mutation<
      AdminApiPlaybook,
      { id: string; branch: PlaybookBranch },
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: ({ id, branch }) => ({
        method: "POST",
        url: `/recording-assessment-templates/${encodeURIComponent(id)}/revert`,
        params: { branch },
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      onQueryStarted: async ({ id, branch }, { dispatch, queryFulfilled }) => {
        const { data: revertedPlaybook } = await queryFulfilled;

        // Update the branch-specific query cache
        dispatch(
          playbookApi.util.updateQueryData(
            "playbookById",
            { id, branch },
            () => revertedPlaybook,
          ),
        );
      },
      invalidatesTags: () => [buildListTag(TagType.Playbook, [])],
    }),

    // TODO: Likely will need to paginate this at some point
    allRecordingAssessmentFeedbackForPlaybook: builder.query<
      readonly FeedbackWithCurrentGrade[],
      string,
      { data: { feedback: readonly FeedbackWithCurrentGrade[] } }
    >({
      query: (templateId) => ({
        method: "GET",
        url: `/recording-assessment-templates/${encodeURIComponent(templateId)}/feedback`,
      }),
      transformResponse: (response) => response.data.feedback,
      providesTags: (result, error, templateId) =>
        getTagsProvidedByList(TagType.RecordingAssessmentFeedback, result, {
          filteredBy: [templateId],
        }),
    }),

    testNodeGrading: builder.mutation<
      TestGradingResult[],
      {
        playbookId: PlaybookId;
        feedbackIds: string[];
        node: PlaybookNode;
      }
    >({
      // The endpoint streams Server-Sent Events with periodic keep-alive
      // heartbeats to avoid Express/Cloudflare timeouts on long grading runs,
      // and emits the final array as a `result` event.
      queryFn: async ({ playbookId, feedbackIds, node }, { signal }) => {
        try {
          const url = new URL(
            `/recording-assessment-templates/${encodeURIComponent(playbookId)}/test-node-grading-stream`,
            apiBaseUrl,
          );
          const events = fetchEventStream(url, {
            headers: {
              authorization: await getApiAuthorizationHeaderValue(),
              "x-alpharun-commit-sha": __COMMIT_SHA__,
            },
            body: {
              feedback_ids: feedbackIds,
              node,
            },
            signal,
          });

          let result: TestGradingResult[] | undefined;
          let serverErrorMessage: string | undefined;
          for await (const { event, data } of events) {
            if (event === "result") {
              result = JSON.parse(data) as TestGradingResult[];
            } else if (event === "error") {
              const parsed = JSON.parse(data) as { message?: string };
              serverErrorMessage = parsed.message ?? "Unknown error";
            }
          }

          if (serverErrorMessage) {
            throw new Error(serverErrorMessage);
          }

          if (!result) {
            throw new Error("Stream ended without a grading result");
          }

          return { data: result };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to test grading",
            },
          };
        }
      },
    }),

    mostRecentlyUsedPlaybookByTeammate: builder.query<
      AdminApiMinimalPlaybook | null,
      string,
      { data: { playbook: AdminApiMinimalPlaybook | null } }
    >({
      query: (teammateId) => ({
        url: `/recording-assessment-templates/most-recently-used-by-teammate/${encodeURIComponent(teammateId)}`,
      }),
      transformResponse: (response) => response.data.playbook,
      providesTags: (result) =>
        result
          ? getTagsProvidedByEntity(TagType.MinimalPlaybook, result.id)
          : [],
    }),

    /**
     * Get published versions of a playbook.
     * Returns all versions that have been published, ordered by most recent first.
     */
    getPlaybookVersions: builder.query<
      AdminApiPlaybookPublishedVersion[],
      string,
      {
        data: {
          versions: AdminApiPlaybookPublishedVersion[];
        };
      }
    >({
      query: (playbookId) =>
        `/recording-assessment-templates/${encodeURIComponent(playbookId)}/versions`,
      transformResponse: (response) => response.data.versions,
      providesTags: (_result, _error, playbookId) =>
        getTagsProvidedByEntity(TagType.Playbook, playbookId),
    }),

    /**
     * Bulk-fetch saved sample-tool result payloads by row id — each
     * envelope carries `id`, `playbook_node_id`, the persisted metadata
     * (`kind`, `parent_section_id`, `candidate_draft`, revision pointers),
     * and the computed `aggregates`. Ids that don't resolve are silently
     * omitted.
     */
    fetchSampleToolResults: builder.query<
      readonly ({
        id: PlaybookNodeSampleToolResultId;
        playbook_node_id: PlaybookNodeId;
      } & PlaybookNodeSampleToolResultPayload)[],
      {
        playbookId: PlaybookId;
        ids: readonly PlaybookNodeSampleToolResultId[];
      },
      {
        data: {
          sample_tool_results: readonly ({
            id: PlaybookNodeSampleToolResultId;
            playbook_node_id: PlaybookNodeId;
          } & PlaybookNodeSampleToolResultPayload)[];
        };
      }
    >({
      query: ({ playbookId, ids }) => ({
        method: "POST",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/fetch-sample-tool-results`,
        body: { ids: [...ids] },
      }),
      transformResponse: (response) => response.data.sample_tool_results,
      providesTags: (result) =>
        result
          ? result.map(({ id }) => ({
              type: TagType.PlaybookSampleToolResult,
              id,
            }))
          : [],
    }),

    /**
     * Hard-delete the saved sample-tool result row for one criterion node.
     * Fired by the editor when the user accepts or discards an AI
     * suggestion.
     */
    deleteSampleToolResultForCriterion: builder.mutation<
      void,
      { playbookId: PlaybookId; playbookNodeId: PlaybookNodeId }
    >({
      query: ({ playbookId, playbookNodeId }) => ({
        method: "DELETE",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/sample-tool-result-for-criteria/${encodeURIComponent(playbookNodeId)}`,
      }),
    }),

    /**
     * Fetch the resolved grading rows for a saved sample-tool result, paired
     * with the tested grades the tool applied. Lazy — triggered when the user
     * opens the "Graded calls" slideover.
     */
    fetchSampleToolResultGradings: builder.query<
      SampleToolResultGradingsPayload,
      {
        playbookId: PlaybookId;
        sampleToolResultId: PlaybookNodeSampleToolResultId;
      },
      { data: SampleToolResultGradingsPayload }
    >({
      query: ({ playbookId, sampleToolResultId }) => ({
        method: "GET",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/sample-tool-result/${encodeURIComponent(sampleToolResultId)}/gradings`,
      }),
      transformResponse: (response) => response.data,
    }),

    /**
     * Rollback a playbook to a previously published version.
     */
    rollbackPlaybook: builder.mutation<
      AdminApiPlaybook,
      { playbookId: string; playbookRevisionId: string },
      { data: { recording_assessment_template: AdminApiPlaybook } }
    >({
      query: ({ playbookId, playbookRevisionId }) => ({
        method: "POST",
        url: `/recording-assessment-templates/${encodeURIComponent(playbookId)}/rollback`,
        body: { playbook_revision_id: playbookRevisionId },
      }),
      transformResponse: (response) =>
        response.data.recording_assessment_template,
      invalidatesTags: (_result, _error, { playbookId }) => [
        ...getTagsInvalidatedByUpdate(TagType.Playbook, playbookId),
        buildListTag(TagType.Playbook, []),
      ],
    }),
  }),
});

export const {
  useAllPlaybooksQuery,
  useAllMinimalPlaybooksQuery,
  usePlaybookByIdQuery,
  useCreatePlaybookMutation,
  useUpdatePlaybookMutation,
  useUpdatePlaybookViewDefaultsMutation,
  useDeletePlaybookMutation,
  useRevertPlaybookBranchMutation,
  useAllRecordingAssessmentFeedbackForPlaybookQuery,
  useTestNodeGradingMutation,
  useMostRecentlyUsedPlaybookByTeammateQuery,
  useGetPlaybookVersionsQuery,
  useRollbackPlaybookMutation,
  useLazyFetchSampleToolResultsQuery,
  useDeleteSampleToolResultForCriterionMutation,
  useLazyFetchSampleToolResultGradingsQuery,
} = playbookApi;
